/* ============================================================================
   HOSTED JOBS — dashboard admin (dalam Flow Ultra, sebelah Session Access)
   ============================================================================
   Pantau queue Central Worker (PC ofis): status, worker health, job, kuota.
   ⛔ SEMUA data dari backend SEBENAR (/admin/*). Tiada data dummy.
   ⛔ SEMUA tindakan admin DISAHKAN DI SERVER (token → semak admin). Lihat hostedJobsAdmin.js.
      · Tanda Siap  → server semak R2 object wujud + tulis Product Card.
      · Tanda Gagal → server pulang kuota HANYA jika Create belum diterima Google.
      · Jana semula → job BARU + kuota BARU (eksplisit), bukan retry senyap.
   Had: 3 card / 9 video sehari / 1 scene (keputusan user 2026-10-29).
   ========================================================================== */
import { useEffect, useState, useCallback } from 'react';
import { RefreshCw, Loader2, Pause, Play } from 'lucide-react';
import { Badge, Btn, Card } from '../design/ui';
import {
  bacaStatsHosted, bacaJobsHosted, setPauseHosted,
  adminTandaSiap, adminTandaGagal, adminJanaSemula,
  bacaRolloutHosted, setRolloutHosted, tambahPilotHosted, buangPilotHosted,
  bacaBatchesHosted, retryBatchEmailHosted,
} from '../services/hostedJobsAdmin';

// Status email batch → rupa badge + sama ada butang retry selamat dipaparkan.
const EMAIL_TONE = { sent: 'ok', sending: 'brand', failed: 'warn', unknown: 'warn', pending: 'neutral', skipped: 'neutral' };
const EMAIL_LABEL = { sent: 'Email dihantar', sending: 'Menghantar…', failed: 'Email gagal', unknown: 'Tak pasti', pending: 'Belum dihantar', skipped: 'Tiada email' };
const RETRY_OK = new Set(['failed', 'unknown', 'pending']); // 'sent' tak boleh retry (elak dua kali); 'skipped' tiada email

const STATUS = {
  dalam_barisan: { label: 'Dalam barisan', tone: 'neutral' },
  sedang_dijana: { label: 'Sedang dijana', tone: 'brand' },
  menyimpan: { label: 'Menyimpan', tone: 'brand' },
  siap: { label: 'Siap', tone: 'ok' },
  perlu_semakan: { label: 'Perlu semakan', tone: 'warn' },
  dibatalkan: { label: 'Dibatalkan', tone: 'neutral' },
};
const FILTERS = [
  { id: 'all', label: 'Semua' },
  { id: 'dalam_barisan', label: 'Dalam barisan' },
  { id: 'sedang_dijana', label: 'Sedang dijana' },
  { id: 'menyimpan', label: 'Menyimpan' },
  { id: 'perlu_semakan', label: 'Perlu semakan' },
  { id: 'siap', label: 'Siap' },
];

const fmtAge = (ms) => {
  if (!ms || ms < 0) return '—';
  const s = Math.round(ms / 1000);
  if (s < 60) return s + 's';
  const m = Math.floor(s / 60);
  if (m < 60) return m + 'm';
  const h = Math.floor(m / 60);
  return h + 'j ' + (m % 60) + 'm';
};
const fmtDur = (ms) => (ms == null ? '—' : (ms < 60000 ? Math.round(ms / 1000) + 's' : Math.floor(ms / 60000) + 'm ' + Math.round((ms % 60000) / 1000) + 's'));
const shortUid = (u) => (u ? (u.length > 10 ? u.slice(0, 6) + '…' + u.slice(-3) : u) : '—');
const setNo = (sk) => (String(sk || '').match(/\d+/) || ['?'])[0];

export default function FlowHostedJobsTab() {
  const [stats, setStats] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [ralat, setRalat] = useState('');
  const [memuat, setMemuat] = useState(true);
  const [filter, setFilter] = useState('all');
  const [cari, setCari] = useState('');
  const [sibuk, setSibuk] = useState('');       // jobId yang sedang diproses tindakan
  const [tglPause, setTglPause] = useState(false);
  const [rollout, setRollout] = useState(null);   // { rollout:'pilot'|'all', pilotUids:[] } — pilot access control
  const [pilotUid, setPilotUid] = useState('');
  const [pilotBusy, setPilotBusy] = useState(false);
  const [batches, setBatches] = useState([]);     // notifikasi batch + status email
  const [batchSibuk, setBatchSibuk] = useState(''); // batchId sedang retry email

  const muat = useCallback(async () => {
    try {
      const [s, j, rw, bw] = await Promise.all([bacaStatsHosted(), bacaJobsHosted({}), bacaRolloutHosted().catch(() => null), bacaBatchesHosted().catch(() => null)]);
      setStats(s); setJobs(j.jobs || []); if (rw) setRollout(rw); if (bw) setBatches(bw.batches || []); setRalat('');
    } catch (e) {
      setRalat(e?.status === 403 ? 'Akaun ini tiada akses admin.' : (e?.message === 'HOSTED_BETA_DISABLED' ? 'Backend Hosted Beta tidak dikonfigur.' : 'Tak dapat sambung ke Central Worker backend.'));
    } finally { setMemuat(false); }
  }, []);

  useEffect(() => {
    let batal = false;
    const tick = () => { if (!batal) muat(); };
    tick();
    const t = setInterval(tick, 10000);
    return () => { batal = true; clearInterval(t); };
  }, [muat]);

  const togglePause = async () => {
    if (!stats) return;
    setTglPause(true);
    try { await setPauseHosted(!stats.paused); await muat(); }
    catch { setRalat('Tak dapat tukar suis Pause.'); }
    finally { setTglPause(false); }
  };

  // ── Pilot access control (rollout berperingkat) ──
  const tukarRollout = async (next) => {
    setPilotBusy(true);
    try { setRollout(await setRolloutHosted(next)); } catch { setRalat('Tak dapat tukar rollout.'); } finally { setPilotBusy(false); }
  };
  const tambahPilot = async () => {
    const u = pilotUid.trim(); if (!u) return;
    setPilotBusy(true);
    try { setRollout(await tambahPilotHosted(u)); setPilotUid(''); } catch { setRalat('Tak dapat tambah pilot.'); } finally { setPilotBusy(false); }
  };
  const buangPilot = async (u) => {
    setPilotBusy(true);
    try { setRollout(await buangPilotHosted(u)); } catch { setRalat('Tak dapat buang pilot.'); } finally { setPilotBusy(false); }
  };

  // Retry email batch — SELAMAT (idempotency key Resend sama → tak hantar dua kali walau sudah separa sampai).
  const retryEmail = async (batchId) => {
    if (!window.confirm('Cuba hantar semula email ringkasan batch ini? Selamat — tidak akan menghantar dua kali.')) return;
    setBatchSibuk(batchId);
    try { await retryBatchEmailHosted(batchId); await muat(); }
    catch (e) { alert(e?.message === 'BATCH_NOT_COMPLETE' ? 'Batch belum selesai sepenuhnya.' : ('Gagal retry: ' + (e?.message || 'ralat'))); }
    finally { setBatchSibuk(''); }
  };

  const tindakan = async (fn, jobId, tanya) => {
    if (tanya && !window.confirm(tanya)) return;
    setSibuk(jobId);
    try { await fn(jobId); await muat(); }
    catch (e) {
      const m = { R2_NOT_VERIFIED: 'Video belum sah di R2 — tak boleh Tanda Siap. Guna Jana semula.', NO_R2_OBJECT: 'Tiada video di R2 untuk job ini. Guna Jana semula.', QUOTA_EXCEEDED: 'Kuota harian pelanggan penuh.', CARD_LIMIT: 'Had card harian pelanggan penuh.' };
      alert(m[e?.message] || ('Gagal: ' + (e?.message || 'ralat')));
    } finally { setSibuk(''); }
  };

  const worker = (stats?.workers || [])[0] || null;
  const anyOnline = (stats?.workers || []).some((w) => w.online);

  const jobsTapis = jobs
    .filter((j) => (filter === 'all' ? true : j.statusPelanggan === filter))
    .filter((j) => {
      if (!cari.trim()) return true;
      const t = (j.uid + ' ' + (j.productName || '') + ' ' + j.productId).toLowerCase();
      return t.includes(cari.trim().toLowerCase());
    });

  const tile = (label, value, color) => (
    <Card><div className="s-stat"><p className="s-stat__lab">{label}</p><p className="s-stat__val" style={color ? { color } : undefined}>{value}</p></div></Card>
  );

  return (
    <>
      <div className="s-sec">
        <div>
          <h2>Hosted Jobs</h2>
          <p>Queue Central Worker (PC ofis) — status langsung, worker health, dan kawalan selamat. Had 3 card · 9 video / hari.</p>
        </div>
        <div style={{ display: 'flex', gap: 8, flex: 'none', alignItems: 'center' }}>
          <Btn variant="ghost" size="sm" icon={memuat ? <Loader2 size={14} className="s-spin" /> : <RefreshCw size={14} />} onClick={muat}>Muat semula</Btn>
          <Btn size="sm" icon={stats?.paused ? <Play size={14} /> : <Pause size={14} />} disabled={tglPause || !stats} onClick={togglePause}>
            {stats?.paused ? 'Resume submission' : 'Pause submission'}
          </Btn>
        </div>
      </div>

      {ralat && <Card><div className="s-row"><span className="s-dot s-dot--stop" /><div className="s-row__main"><p className="s-row__name">{ralat}</p><p className="s-row__sub">Dashboard membaca data sebenar sahaja — tiada paparan sementara.</p></div><div className="s-row__right"><Btn variant="ghost" size="sm" onClick={muat}>Cuba semula</Btn></div></div></Card>}

      {stats && (
        <>
          {stats.paused && <div style={{ marginBottom: 14 }}><Card style={{ borderColor: 'var(--s-warn)' }}><div className="s-row"><span className="s-dot s-dot--warn" /><div className="s-row__main"><p className="s-row__name">Submission baru DI-PAUSE</p><p className="s-row__sub">Pelanggan tidak boleh hantar job baharu sehingga Resume. Job sedia ada tetap diproses.</p></div></div></Card></div>}

          {rollout && (
            <div style={{ marginBottom: 14 }}>
              <Card style={rollout.rollout === 'all' ? { borderColor: 'var(--s-warn)' } : undefined}>
                <div className="s-row">
                  <div className="s-row__main">
                    <p className="s-row__name">Akses Hosted Beta: {rollout.rollout === 'all' ? 'SEMUA penyewa Lite LP aktif' : `Pilot sahaja — ${rollout.pilotUids.length} UID`}</p>
                    <p className="s-row__sub">Default tutup. Pelanggan masih WAJIB langganan Lite LP aktif. Buka 'Semua' hanya bila sedia rollout penuh.</p>
                  </div>
                  <div className="s-row__right" style={{ gap: 6 }}>
                    <Btn variant={rollout.rollout === 'pilot' ? undefined : 'ghost'} size="sm" disabled={pilotBusy} onClick={() => tukarRollout('pilot')}>Pilot</Btn>
                    <Btn variant={rollout.rollout === 'all' ? undefined : 'ghost'} size="sm" disabled={pilotBusy} onClick={() => { if (window.confirm('Buka Hosted Beta kepada SEMUA penyewa Lite LP aktif?')) tukarRollout('all'); }}>Semua</Btn>
                  </div>
                </div>
                <div className="s-row" style={{ gap: 8, flexWrap: 'wrap' }}>
                  <input value={pilotUid} onChange={(e) => setPilotUid(e.target.value)} placeholder="UID pilot untuk diluluskan…" style={{ flex: '1 1 220px', maxWidth: 340, height: 32, padding: '0 10px', borderRadius: 8, border: '1px solid var(--s-line)', background: 'var(--s-bg)', color: 'var(--s-ink)', fontSize: 13 }} />
                  <Btn size="sm" disabled={pilotBusy || !pilotUid.trim()} onClick={tambahPilot}>Tambah pilot</Btn>
                </div>
                {rollout.pilotUids.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, padding: '0 16px 12px' }}>
                    {rollout.pilotUids.map((u) => (
                      <span key={u} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, padding: '3px 8px', borderRadius: 999, background: 'var(--s-ink-7, #eef)', color: 'var(--s-ink-1)' }}>
                        <span className="s-num">{u.length > 14 ? u.slice(0, 10) + '…' : u}</span>
                        <button type="button" disabled={pilotBusy} onClick={() => buangPilot(u)} style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--s-ink-3)', fontSize: 14, lineHeight: 1 }}>×</button>
                      </span>
                    ))}
                  </div>
                )}
              </Card>
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0,1fr))', gap: 12, marginBottom: 16 }}>
            {tile('Dalam barisan', stats.waiting)}
            {tile('Sedang dijana', stats.generating + stats.saving, 'var(--s-brand)')}
            {tile('Siap hari ni', stats.doneToday, 'var(--s-ok, #1d9e75)')}
            {tile('Perlu semakan', stats.needsReview, stats.needsReview > 0 ? 'var(--s-warn)' : undefined)}
          </div>

          <div style={{ marginBottom: 16 }}>
            <Card>
              <div className="s-row">
                <span className={`s-dot ${anyOnline ? 's-dot--ok' : 's-dot--stop'}`} />
                <div className="s-row__main">
                  <p className="s-row__name">{anyOnline ? 'Worker online' : 'Worker offline'}</p>
                  <p className="s-row__sub">
                    {worker ? <>heartbeat {fmtAge(worker.ageMs)} lalu{worker.profile ? ` · profile ${worker.profile}` : ''}{worker.account ? ` · ${worker.account}` : ''}{worker.loginOk === false ? ' · login bermasalah' : ''}</> : 'Belum ada worker melapor diri (PC ofis belum hidup).'}
                  </p>
                </div>
                <div className="s-row__right">
                  <span className="s-dim" style={{ fontSize: 12.5 }}>purata {fmtDur(stats.avgGenMs)} · job tertua {fmtAge(stats.oldestPendingMs)}</span>
                </div>
              </div>
            </Card>
          </div>

          {Array.isArray(stats.customers) && stats.customers.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <Card>
                <div className="s-card__head" style={{ padding: '12px 16px' }}><p className="s-cardname">Kuota harian / pelanggan</p></div>
                {stats.customers.map((c) => (
                  <div key={c.uid} className="s-row">
                    <div className="s-row__main"><p className="s-row__name s-num" style={{ fontSize: 13 }}>{shortUid(c.uid)}</p></div>
                    <div className="s-row__right">
                      <Badge tone={c.remaining <= 0 ? 'warn' : 'neutral'}>{c.used}/{c.cap} video</Badge>
                      <Badge tone={c.cardsRemaining <= 0 ? 'warn' : 'neutral'}>{c.cards}/{c.cardCap} card</Badge>
                    </div>
                  </div>
                ))}
              </Card>
            </div>
          )}

          {batches.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <Card>
                <div className="s-card__head" style={{ padding: '12px 16px' }}><p className="s-cardname">Notifikasi batch — status email</p></div>
                {batches.slice(0, 12).map((b) => {
                  const em = b.email || {};
                  const tone = EMAIL_TONE[em.status] || 'neutral';
                  return (
                    <div key={b.batchId} className="s-row">
                      <div className="s-row__main">
                        <p className="s-row__name s-num" style={{ fontSize: 13 }}>{shortUid(b.uid)} <span className="s-dim" style={{ fontWeight: 400 }}>· {b.accepted}/{b.requestedCount} video · {b.status}</span></p>
                        <p className="s-row__sub">
                          submit {fmtAge(Date.now() - (b.createdAt || 0))} lalu
                          {em.sentAt ? ` · dihantar ${fmtAge(Date.now() - em.sentAt)} lalu` : ''}
                          {em.resendId ? ` · id ${String(em.resendId).slice(0, 10)}` : ''}
                          {em.error ? ` · ${String(em.error).slice(0, 40)}` : ''}
                          {em.attempts ? ` · cuba ${em.attempts}×` : ''}
                        </p>
                      </div>
                      <div className="s-row__right" style={{ gap: 6 }}>
                        <Badge tone={tone === 'ok' ? 'brand' : tone}>{EMAIL_LABEL[em.status] || em.status || '—'}</Badge>
                        {RETRY_OK.has(em.status) && (
                          <Btn variant="ghost" size="sm" disabled={batchSibuk === b.batchId}
                            icon={batchSibuk === b.batchId ? <Loader2 size={13} className="s-spin" /> : undefined}
                            onClick={() => retryEmail(b.batchId)}>Retry email</Btn>
                        )}
                      </div>
                    </div>
                  );
                })}
              </Card>
            </div>
          )}

          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
            {FILTERS.map((f) => (
              <button key={f.id} type="button" className={`fu-task${filter === f.id ? ' fu-task--on' : ''}`} style={{ padding: '5px 12px', width: 'auto' }} aria-pressed={filter === f.id} onClick={() => setFilter(f.id)}>
                <span className="fu-task__lbl" style={{ flexDirection: 'row', gap: 6 }}><b>{f.label}</b></span>
              </button>
            ))}
            <input value={cari} onChange={(e) => setCari(e.target.value)} placeholder="Cari pelanggan / card…" style={{ flex: '1 1 220px', maxWidth: 300, height: 34, padding: '0 12px', borderRadius: 8, border: '1px solid var(--s-line)', background: 'var(--s-bg)', color: 'var(--s-ink)', fontSize: 13 }} />
          </div>

          <Card>
            {jobsTapis.length === 0 ? (
              <div className="s-row"><div className="s-row__main"><p className="s-row__sub">{jobs.length === 0 ? 'Belum ada job. Job muncul di sini sebaik pelanggan tekan Submit.' : 'Tiada job sepadan tapisan.'}</p></div></div>
            ) : jobsTapis.map((j) => {
              const st = STATUS[j.statusPelanggan] || { label: j.statusPelanggan, tone: 'neutral' };
              const semakan = j.statusPelanggan === 'perlu_semakan';
              return (
                <div key={j.id} className="s-row" style={semakan ? { background: 'var(--s-warn-7, rgba(239,159,39,.06))' } : undefined}>
                  <span className={`s-dot ${st.tone === 'ok' ? 's-dot--ok' : st.tone === 'warn' ? 's-dot--warn' : st.tone === 'brand' ? 's-dot--brand' : ''}`} />
                  <div className="s-row__main">
                    <p className="s-row__name s-num" style={{ fontSize: 13 }}>{shortUid(j.uid)} <span className="s-dim" style={{ fontWeight: 400 }}>· {j.productName || j.productId} · set {setNo(j.setKey)}</span></p>
                    <p className="s-row__sub">submit {fmtAge(Date.now() - (j.submitAt || 0))} lalu{j.error ? ` · ${String(j.error).slice(0, 48)}` : ''}</p>
                  </div>
                  <div className="s-row__right" style={{ gap: 6 }}>
                    <Badge tone={st.tone === 'ok' ? 'brand' : st.tone}>{st.label}</Badge>
                    {semakan && (
                      <>
                        <Btn variant="ghost" size="sm" disabled={sibuk === j.id} onClick={() => tindakan(adminTandaSiap, j.id, 'Tanda Siap? Server akan sahkan video wujud di R2 dahulu.')}>Tanda siap</Btn>
                        <Btn variant="ghost" size="sm" disabled={sibuk === j.id} onClick={() => tindakan(adminTandaGagal, j.id, 'Tanda Gagal? Kuota dipulang hanya jika Create belum dihantar Google.')}>Tanda gagal</Btn>
                        <Btn size="sm" disabled={sibuk === j.id} icon={sibuk === j.id ? <Loader2 size={13} className="s-spin" /> : undefined} onClick={() => tindakan(adminJanaSemula, j.id, 'Jana semula? Ini job BARU + guna kuota BARU pelanggan.')}>Jana semula</Btn>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </Card>
        </>
      )}
    </>
  );
}
